import React, {
  memo,
  useRef,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from "react";
import { Sprite } from "@pixi/react";
import * as PIXI from "pixi.js";
import anime from "animejs";
import { Settings } from "../../../settings";
import { OVERLAY_BACKGROUND_COLOR } from "../../Shared/Overlay/OverlayController";

// Dimmed symbol alpha (calculated from tint 0x555555)
const DIMMED_ALPHA = 0.6;

// Per-reel dimming overlay for tension mode and other effects
const DimReelOverlay = memo(
  forwardRef(({ symbolWidth, symbolHeight }, ref) => {
    const dimSpriteRef = useRef(null);
    const dimAnimRef = useRef(null);
    const currentAlphaRef = useRef(0); // Track current alpha to persist across re-renders

    // Expose dimming control methods through ref
    useImperativeHandle(
      ref,
      () => ({
        // Dim the reel (show overlay)
        dimReel: () => {
          if (!dimSpriteRef.current) {
            return;
          }

          // Cancel any existing animation
          if (dimAnimRef.current) {
            dimAnimRef.current.pause();
          }

          currentAlphaRef.current = DIMMED_ALPHA;

          dimAnimRef.current = anime({
            targets: dimSpriteRef.current,
            alpha: DIMMED_ALPHA,
            duration: 150,
            delay: 700,
            easing: "easeOutQuad",
          });
        },

        // Remove dimming (clear overlay)
        undimReel: () => {
          if (!dimSpriteRef.current) {
            return;
          }

          // Cancel any existing animation
          if (dimAnimRef.current) {
            dimAnimRef.current.pause();
          }

          currentAlphaRef.current = 0;

          dimAnimRef.current = anime({
            targets: dimSpriteRef.current,
            alpha: 0,
            duration: 60,
            easing: "easeInQuad",
          });
        },
      }),
      []
    );

    // Preserve alpha state across re-renders (especially during window resize)
    useEffect(() => {
      if (dimSpriteRef.current && currentAlphaRef.current !== 0) {
        // Restore the alpha without animation when dimensions change
        dimSpriteRef.current.alpha = currentAlphaRef.current;
      }
    }, [symbolWidth, symbolHeight]);

    // Cleanup animation on unmount to prevent memory leaks
    useEffect(() => {
      return () => {
        if (dimAnimRef.current) {
          dimAnimRef.current.pause();
          dimAnimRef.current = null;
        }
      };
    }, []);

    return (
      <Sprite
        ref={dimSpriteRef}
        texture={PIXI.Texture.WHITE}
        width={symbolWidth}
        height={symbolHeight * Settings.SYMBOLS_VISIBLE}
        anchor={0.5}
        tint={OVERLAY_BACKGROUND_COLOR}
        alpha={0}
        y={symbolHeight * Settings.SYMBOLS_VISIBLE * 1.5 + symbolHeight * 0.5}
        name="dim_reel_sprite"
      />
    );
  })
);

DimReelOverlay.displayName = "DimReelOverlay";

export default DimReelOverlay;
